import React from "react";
import { useCollection, SSRSuspense, denormalize, ErrorBoundary } from "coreql";
import {
  Frame,
  Text,
  Stack,
  Inline,
  Center,
  Grid,
  Cell,
  Skeleton,
  Color,
} from "@ableco/baseline";
import { getShortTimeZone, getStandardTimeZone } from "../../utils/time-zone";
import removeAccents from "../../utils/remove_accents";
import { Magnifier } from "@baseline/icons";
import CoreLink from "../core-link";
import { wrap } from "../weekly-report/utils";
import PageLayout from "../layout/page-layout";
import { Heading } from "../design-system/typography-components";
import TextInput from "../text-input";
import { AvatarXXL } from "../design-system/avatar";

function Legend({ children }) {
  return (
    <Frame p={[8, 3, 6, 3]}>
      <Text className="text-neutral-800" size="sm">
        {children}
      </Text>
    </Frame>
  );
}

function User({ avatarUrl, fullName, id, role }) {
  return (
    <CoreLink
      href={`/users/${id}`}
      className="cursor-pointer w-1/5 py-4"
      leading="normal"
      prefetch={false}
    >
      <Stack as="figure" alignment="center">
        <AvatarXXL
          className="hover:shadow-outline transition-shadow ease-in-out duration-300"
          url={avatarUrl}
          name={fullName}
        />

        <Frame as="figcaption" className="text-center mt-3">
          <Text
            as="p"
            size="md"
            color="primary-darker"
            weight="semibold"
            leading="normal"
          >
            {fullName}
          </Text>
          {role?.title && (
            <>
              <Text
                as="p"
                size="xs"
                leading="normal"
                className="text-neutral-800"
              >
                {role.title}
              </Text>
            </>
          )}
        </Frame>
      </Stack>
    </CoreLink>
  );
}

function getInitials(value) {
  return value
    .split(" ")
    .map((word) => word[0])
    .join("");
}

function UserList({ filter }) {
  const results = useCollection("users", {
    included: ["role"],
    filters: { active: true },
    fields: {
      users: ["avatarUrl", "fullName", "firstName", "role"],
      roles: ["title"],
    },
  });
  const users = wrap(denormalize(results.data, "users"));
  const fullLength = users.length;
  const filteredList = users.filter((user) => {
    const lowerCaseName = user?.fullName.toLowerCase();
    const lowerCaseFirstName = user?.firstName.toLowerCase();
    const lowerCaseFilter = filter.toLowerCase();
    const lowerCaseRole = user?.role?.title.toLowerCase() ?? "";
    return (
      removeAccents(lowerCaseName).includes(lowerCaseFilter) ||
      lowerCaseName.includes(lowerCaseFilter) ||
      removeAccents(lowerCaseFirstName).includes(lowerCaseFilter) ||
      lowerCaseFirstName.includes(lowerCaseFilter) ||
      getInitials(lowerCaseName).includes(lowerCaseFilter) ||
      lowerCaseRole.includes(lowerCaseFilter) ||
      getInitials(lowerCaseRole).includes(lowerCaseFilter)
    );
  });
  const filteredLength = filteredList.length;
  return (
    <>
      {filteredLength > 0 && (
        <Legend>
          Displaying {filteredLength} of {fullLength} people
        </Legend>
      )}
      <Inline as="section" alignment="start" wrap p={2}>
        {filteredLength > 0 ? (
          [...filteredList] // copy the array to sort it later
            .sort((a, b) => a.fullName.localeCompare(b.fullName))
            .map((user) => <User {...user} key={user.id} />)
        ) : (
          <Stack
            as="div"
            alignment="center"
            distribution="center"
            className="w-full"
            p={10}
          >
            <Stack
              as="figure"
              alignment="center"
              distribution="center"
              bg="neutral-400"
              corners="full"
              className="w-40 h-40 m-8"
            >
              <EmptyStateDraw />
            </Stack>
            <Text
              as="h3"
              size="2xl"
              weight="semibold"
              leading="normal"
              color="neutral-800"
              className="mb-3"
            >
              We don't see anyone that matches your search
            </Text>
            <Text as="p" size="base" color="neutral-800">
              Try searching for another name or role
            </Text>
          </Stack>
        )}
      </Inline>
    </>
  );
}

function UserListFallback() {
  return (
    <Stack>
      <Legend>Displaying X of Y people</Legend>
      <Inline p={2} wrap>
        {Array.from({ length: 30 }, (_, i) => (
          <Stack
            alignment="center"
            p={[4, 0]}
            className="cursor-pointer w-1/5"
            key={i}
          >
            <Skeleton
              color="neutral-400"
              size={128}
              corners="full"
              className="w-32 h-32"
            />
            <Skeleton
              color="neutral-400"
              height={16}
              width="66%"
              className="mt-3"
            />
            <Skeleton
              color="neutral-400"
              height={12}
              width="33%"
              className="mt-2"
            />
          </Stack>
        ))}
      </Inline>
    </Stack>
  );
}

function ErrorFallback({ error }) {
  return <Legend>{error.message}</Legend>;
}

function Locations() {
  const { data: locations } = useCollection(
    "locations",
    {
      filters: { inDirectory: true, active: true },
    },
    "denormalized",
  );

  return (
    <Frame corners="medium" bg="white" p={5}>
      <Text
        as="h2"
        color="black"
        transform="upper"
        size="xs"
        weight="bold"
        className="mb-3"
      >
        Locations
      </Text>
      {[...locations] // copy the array to sort it later
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((location) => (
          <Frame as="article" key={location.id} className="mb-5">
            <Text as="address" italic={false} size="xs" leading="tight">
              <Text
                as="strong"
                weight="bold"
                decoration="underline"
                leading="normal"
                size="sm"
              >
                {location.name}
              </Text>
              <Frame as="br" />
              {location.address1}
              <Frame as="br" />
              {location.address2}
              <Frame as="br" />
              <Text
                as="em"
                italic={false}
                size="xs"
                className="text-neutral-800"
                leading="normal"
              >
                {new Date(location.currentLocalTime).toLocaleTimeString(
                  "en-US",
                  {
                    timeStyle: "short",
                    timeZone: getStandardTimeZone(location.timeZone),
                  },
                )}{" "}
                ({getShortTimeZone(location.timeZone)})
              </Text>
            </Text>
          </Frame>
        ))}
    </Frame>
  );
}

function EmptyStateDraw(props) {
  return (
    <svg width="85px" height="85px" viewBox="0 0 85 85" {...props}>
      <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
        <path
          d="M17.106 76.633c-.09-1.816-2.024-5.131-5.366-5.49 1.627.134 5.189-2.158 5.527-5.408 0 0 .405 4.548 5.372 5.492-1.87-.342-5.672 2.964-5.533 5.406zM17.351 60C16.658 66.673 9.34 71.371 6 71.098c6.868.74 10.836 7.542 11.025 11.272-.29-5.012 7.512-11.799 11.351-11.096C18.182 69.332 17.351 60 17.351 60zM32.462 82c-.042-.833-.929-2.354-2.462-2.519.746.061 2.38-.99 2.535-2.481 0 0 .186 2.086 2.465 2.52-.858-.157-2.602 1.36-2.538 2.48zm.118-8c-.34 3.281-3.938 5.592-5.58 5.457 3.376.364 5.327 3.71 5.42 5.543-.143-2.464 3.693-5.802 5.58-5.456C32.989 78.589 32.58 74 32.58 74zM20.97 8c-.034-.667-.743-1.883-1.97-2.015.597.049 1.904-.792 2.028-1.985 0 0 .15 1.67 1.972 2.016-.686-.126-2.082 1.088-2.03 1.984zm.088-6C20.811 4.386 18.194 6.067 17 5.969c2.456.265 3.874 2.697 3.941 4.031-.103-1.792 2.686-4.22 4.059-3.968C21.355 5.337 21.058 2 21.058 2zM82.477 47c-.025-.5-.557-1.413-1.477-1.511.448.036 1.428-.594 1.521-1.489 0 0 .112 1.252 1.479 1.512-.515-.094-1.561.816-1.523 1.488zm.06-4c-.155 1.491-1.79 2.542-2.537 2.48 1.535.166 2.421 1.686 2.463 2.52-.064-1.12 1.68-2.637 2.537-2.48-2.278-.434-2.464-2.52-2.464-2.52zM7.97 33c-.034-.667-.743-1.883-1.97-2.015.597.049 1.904-.792 2.028-1.985 0 0 .15 1.67 1.972 2.016-.686-.126-2.082 1.088-2.03 1.984zm.088-6C7.811 29.386 5.194 31.067 4 30.969c2.456.265 3.874 2.697 3.941 4.031-.103-1.792 2.686-4.22 4.059-3.968C8.355 30.337 8.058 27 8.058 27zM43.477 8c-.025-.5-.557-1.413-1.477-1.511.448.036 1.428-.594 1.521-1.489 0 0 .112 1.252 1.479 1.512-.515-.094-1.561.816-1.523 1.488zm.06-4c-.155 1.491-1.79 2.542-2.537 2.48 1.535.166 2.421 1.686 2.463 2.52C43.4 7.88 45.143 6.363 46 6.52 43.722 6.086 43.536 4 43.536 4zM74.94 28c-.066-1.334-1.485-3.765-3.94-4.03 1.195.097 3.81-1.58 4.057-3.97 0 0 .298 3.336 3.943 4.033-1.373-.25-4.162 2.176-4.06 3.967zm.206-14c-.62 5.967-7.16 10.166-10.146 9.925 6.138.658 9.684 6.741 9.852 10.075-.258-4.483 6.715-10.549 10.148-9.92C75.888 22.345 75.146 14 75.146 14zM65.463 10c-.042-.833-.928-2.353-2.463-2.519.747.061 2.381-.988 2.536-2.481 0 0 .186 2.085 2.464 2.52-.858-.156-2.601 1.36-2.537 2.48zm.132-9C65.19 4.878 60.94 7.608 59 7.451c3.99.428 6.294 4.382 6.404 6.549-.168-2.914 4.365-6.857 6.596-6.448C66.077 6.424 65.595 1 65.595 1zM13.594 8s.481 5.424 6.406 6.551c-2.232-.406-6.763 3.537-6.596 6.449-.108-2.164-2.414-6.12-6.404-6.55 1.938.159 6.19-2.572 6.594-6.45zM79.058 50s.296 2.92 3.942 3.528c-1.374-.22-4.162 1.904-4.059 3.472-.067-1.165-1.485-3.295-3.941-3.527 1.192.086 3.81-1.385 4.058-3.473zM4.565 35S4.898 38.755 9 39.536C7.454 39.254 4.318 41.984 4.434 44 4.358 42.502 2.763 39.764 0 39.466c1.341.11 4.285-1.781 4.565-4.466zM38.043 0S38.265 2.503 41 3.024C39.97 2.836 37.878 4.656 37.956 6c-.05-.999-1.114-2.824-2.956-3.023.894.073 2.857-1.187 3.043-2.977zM80.565 3S80.898 6.755 85 7.536c-1.546-.282-4.682 2.448-4.566 4.464-.076-1.498-1.671-4.236-4.434-4.534 1.341.11 4.285-1.781 4.565-4.466zM40.565 66s.333 3.755 4.435 4.536c-1.546-.282-4.682 2.448-4.566 4.464-.076-1.498-1.671-4.236-4.434-4.534 1.341.11 4.285-1.781 4.565-4.466z"
          fill="#8C99B0"
        />
        <path
          d="M42.984 36.356a23.227 23.227 0 003.891-12.919C46.875 10.514 36.361 0 23.437 0 10.514 0 0 10.514 0 23.438 0 36.36 10.514 46.874 23.438 46.874c4.635 0 9.08-1.342 12.918-3.89l4.324 4.323.002.002 11.316 11.317A4.657 4.657 0 0055.313 60a4.656 4.656 0 003.313-1.373A4.655 4.655 0 0060 55.312c0-1.252-.488-2.429-1.373-3.314L42.984 36.356z"
          fill="#FFF"
          transform="translate(16 13)"
        />
        <path
          d="M36.328 22.266a1.172 1.172 0 10.001 2.344 1.172 1.172 0 000-2.344"
          fill="#8C99B0"
          transform="translate(16 13)"
        />
        <path
          d="M56.97 56.97a2.328 2.328 0 01-1.657.686 2.328 2.328 0 01-1.657-.687L43.25 46.564a30.794 30.794 0 003.314-3.314L56.97 53.656c.442.442.686 1.03.686 1.657 0 .626-.244 1.214-.687 1.656zM2.343 23.437c0-11.632 9.462-21.094 21.093-21.094 11.632 0 21.094 9.462 21.094 21.093 0 4.474-1.389 8.75-4.016 12.368a21.196 21.196 0 01-4.71 4.71 20.913 20.913 0 01-12.367 4.016c-11.632 0-21.094-9.462-21.094-21.093zm35.92 18.14a23.517 23.517 0 003.313-3.314l3.325 3.324a28.445 28.445 0 01-3.314 3.314l-3.324-3.325zm4.72-5.222a23.227 23.227 0 003.891-12.919C46.875 10.514 36.361 0 23.437 0 10.514 0 0 10.514 0 23.438 0 36.36 10.514 46.874 23.438 46.874c4.635 0 9.08-1.342 12.918-3.89l4.324 4.323.002.002 11.316 11.317A4.657 4.657 0 0055.313 60a4.656 4.656 0 003.313-1.373A4.655 4.655 0 0060 55.312c0-1.252-.488-2.429-1.373-3.314L42.984 36.356z"
          fill="#8C99B0"
          transform="translate(16 13)"
        />
        <path
          d="M23.438 39.844c-9.047 0-16.407-7.36-16.407-16.407 0-9.046 7.36-16.406 16.407-16.406 9.046 0 16.406 7.36 16.406 16.407 0 9.046-7.36 16.406-16.407 16.406zm18.75-16.407c0-10.338-8.412-18.75-18.75-18.75-10.34 0-18.75 8.412-18.75 18.75 0 10.34 8.41 18.75 18.75 18.75 10.338 0 18.75-8.41 18.75-18.75z"
          fill="#8C99B0"
          transform="translate(16 13)"
        />
        <path
          d="M35.897 19.921c.604-.232.905-.91.673-1.514a14.063 14.063 0 00-5.081-6.494 13.963 13.963 0 00-8.052-2.538 1.172 1.172 0 000 2.344c4.817 0 9.215 3.025 10.945 7.529a1.172 1.172 0 001.515.673"
          fill="#8C99B0"
          transform="translate(16 13)"
        />
      </g>
    </svg>
  );
}

export default function PeopleDirectory() {
  const [query, setQuery] = React.useState("");

  return (
    <Stack>
      <PageLayout title="Team">
        <Grid gap={10} col={12} p={10} bg="neutral-300" className="-mx-8">
          <Cell start={1} end={10}>
            <Frame as="section" corners="medium" bg="white" p={[5, 8]}>
              <Frame as="header" p={[6, 0, 0, 0]}>
                <Heading color={Color.PrimaryDarker}>Team Members</Heading>
                <Frame as="form" className="mt-5 relative w-3/4">
                  <Center
                    as="i"
                    p={3}
                    bg="transparent"
                    className="absolute top-0 left-0 bottom-0 pointer-events-none"
                  >
                    <Text color="neutral-500">
                      <Magnifier />
                    </Text>
                  </Center>
                  <TextInput
                    value={query}
                    placeholder="Search by name or role"
                    aria-label="Search by name or role"
                    type="search"
                    onChange={(event) => setQuery(event.target.value)}
                    className="pl-10"
                  />
                </Frame>
              </Frame>
              <ErrorBoundary fallback={ErrorFallback}>
                <SSRSuspense fallback={<UserListFallback />}>
                  <UserList filter={query} />
                </SSRSuspense>
              </ErrorBoundary>
            </Frame>
          </Cell>

          <Cell start={10} end={13}>
            <Frame as="section">
              <ErrorBoundary fallback={ErrorFallback}>
                <SSRSuspense fallback={<Legend>Loading locations</Legend>}>
                  <Locations />
                </SSRSuspense>
              </ErrorBoundary>
            </Frame>
          </Cell>
        </Grid>
      </PageLayout>
    </Stack>
  );
}
